import {StringUtil,} from '@services/util/string-util';
import {Decimal,} from 'decimal.js';
import * as moment from 'moment';
import {MessageStackService,} from '@services/message/message-stack.service';
import {MessageLevel,} from '@models/system-vo/message-level';
import {Injectable,} from '@angular/core';

/**
 * Castユーティリティー
 */
@Injectable({
  providedIn: 'root',
})
export class CastUtil {
  readonly DATE_FORMAT_VB_YEAR = 'y';
  readonly DATE_FORMAT_VB_DAY = 'd';
  readonly DATE_FORMAT_MOMENTJS_YEAR = 'Y';
  readonly DATE_FORMAT_MOMENTJS_DAY = 'D';
  readonly DATE_FORMAT_MOMENTJS_YY = 'YY';
  readonly REGEX_SINGLE_Y = /(?<!Y)Y(?!Y)/g;

  constructor(
    private stringUtil: StringUtil
  ) {}

  /**
   * [一般開発者利用可]
   * stringをnumberに変換（変換時にROUND_HALF_EVEN実施)
   * @param str 変換対象の文字列
   * @returns number変換の結果
   */
  parseNumber(str: string): number {

    // nullの場合、0を返す
    if (str == null) {
      return 0;
    }

    // 入力値をROUND_HALF_EVENで四捨五入しnumberに変換
    if (this.stringUtil.isNumeric(str)) {
      return this.numberRoundHalfDown(Number(str.replace(/,/g, '')));
    } else {
      // 入力値が数字形ではない場合、エラーメッセージを表示
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M007E',});
      throw messageStack;
    }
  }

  /**
   * [一般開発者利用可]
   * stringをDecimalに変換
   * @param str 変換対象の文字列
   * @returns Decimal変換の結果
   */
  parseDecimal(str: string): Decimal {

    // nullの場合、0を返す
    if (str == null) {
      return new Decimal(0);
    }

    // 入力値をDecimalに変換
    if (this.stringUtil.isNumeric(str)) {
      return new Decimal(this.stringUtil.toHalfwidth(str).replace(/,/g, ''));
    } else {
      // 入力値が数字形ではない場合、エラーメッセージを表示
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M007E',});
      throw messageStack;
    }
  }

  /**
   * [一般開発者利用可]
   * stringをDecimalに変換（変換時にROUND_HALF_EVEN実施)
   * @param str 変換対象の文字列
   * @returns Decimal変換の結果
   */
  parseInteger(str: string): Decimal {

    // nullの場合、0を返す
    if (str == null) {
      return new Decimal(0);
    }

    // 入力値をROUND_HALF_EVENで四捨五入しDecimalに変換
    if (this.stringUtil.isNumeric(str)) {
      return this.decimalRoundHalfDown(new Decimal(this.stringUtil.toHalfwidth(str).replace(/,/g, '')));
    } else {
      // 入力値が数字形ではない場合、エラーメッセージを表示
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M007E',});
      throw messageStack;
    }
  }

  /**
   * [一般開発者利用可]
   * stringをDateに変換
   * @param str 変換対象の文字列
   * @param format strの日付書式
   * @returns Date変換の結果
   */
  parseDate(str: string, format: string): Date {

    // 入力値が有効な場合、日付に変換
    const val = moment(str, this.convertDateFormatForMomentJs(format));
    if (val.isValid() && val.year() > 0 && val.year() <= 9999) {
      return val.toDate();
    } else {
      // 入力値が日付形ではないまたは日付範囲外の場合、エラーメッセージを表示
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M007E',});
      throw messageStack;
    }
  }

  /**
   * [一般開発者利用可]
   * stringをbooleanに変換
   * @param val string | number
   * @returns boolean
   */
  parseBoolean(val: string | number): boolean {
    switch (val) {
      case 'true':
      case 1:
      case '1':
      case 'on':
      case 'yes':
        return true;
      default:
        return false;
    }
  }

  /**
   * [一般開発者利用可]
   * データがnumber型の場合にROUND_HALF_EVENで四捨五入を実施
   * @param val 四捨五入処理対象number
   * @returns ROUND_HALF_EVENで四捨五入した結果
   */
  numberRoundHalfDown(val: number): number {
    if ((val - Math.floor(val) > 0.5) ||
      (val - Math.floor(val) === 0.5 && Math.abs(Math.floor(val)) % 2 === 1)) {
      // 小数分が0.5より高いまたは、小数分が0.5かつ整数分が奇数の場合、切り上げ結果を返す
      return Math.ceil(val);
    } else {
      // 小数分が0.5より低いまたは、小数分が0.5かつ整数分が偶数の場合、切り下げ結果を返す
      return Math.floor(val);
    }
  }

  /**
   * [一般開発者利用可]
   * データがDecimal型の場合にROUND_HALF_EVENで四捨五入を実施
   * @param val 四捨五入処理対象Decimal
   * @returns ROUND_HALF_EVENで四捨五入した結果
   */
  decimalRoundHalfDown(val: Decimal): Decimal {

    // nullの場合、0を返す
    if (val == null) {
      return new Decimal(0);
    }

    // Decimal型でない場合、Decimal型に変換
    if (!(val instanceof Decimal)) {
      val = new Decimal(val);
    }

    // 四捨五入方法をROUND_HALF_EVENに設定し四捨五入処理を実施
    Decimal.set({rounding: Decimal.ROUND_HALF_EVEN,});
    return val.round();
  }

  /**
   * [一般開発者利用可]
   * 日付書式変換（VB.Netの日付書式 → Moment.jsの日付書式）
   * @param vbDateFormat VB.Netの日付書式
   * @returns Moment.jsの日付書式
   */
   convertDateFormatForMomentJs(vbDateFormat: string): string {

    const tsDateFormat = vbDateFormat.replaceAll(this.DATE_FORMAT_VB_YEAR, this.DATE_FORMAT_MOMENTJS_YEAR)
      .replaceAll(this.DATE_FORMAT_VB_DAY, this.DATE_FORMAT_MOMENTJS_DAY)
      .replaceAll(this.REGEX_SINGLE_Y, this.DATE_FORMAT_MOMENTJS_YY);

    return tsDateFormat;
  }
}
